import Vue from "vue";
import Vuex from "vuex";
import axios from "axios";
import createPersistedState from "vuex-persistedstate";
import config from '@/config';
import Cookies from 'js-cookie';
import axiosInstance from '@/api/axios';
import cart from './modules/cart';
import batch from './modules/batch';

Vue.use(Vuex);


const api=axios.create({
  baseURL: config.apiUrl,
});

// Add Authorization header interceptor to store's api instance
api.interceptors.request.use((config) => {
  const token=Cookies.get("token");
  if (token) {
    config.headers.Authorization=`Bearer ${token}`;
  }
  return config;
});

export default new Vuex.Store({  
  modules: {
    cart,
    batch,
  },
  state: {
    status: "",
    token: Cookies.get("token")||"",
    tokenExpiryDate: Cookies.get("tokenExpiryDate")||null,
    user: {},
    categories: {},
    categoriesLoaded: false,
    messagesLoaded: false,
    attributes: {},
    store: {
      user_id: "",
      name: "",
      address: "",
      picture: "",
      phone_number: "",
      verified: ""
    },
    product: {
      name: "",
      category: "",
      pictures: [],
      salesType: "",
      fixPrice: "",
      buyItNowPrice: "",
      lowestPrice: "",
      startingPrice: "",
      quantity: "",
      state: "",
      description: "",
      duration: "",
      attributes: {},
      acceptOffers: false,
      acceptCashOnDelivery: false,
      acceptReturn: true,
      searchFilter: []
    },
    selectedCategory: '',
    messages: [],
    notifications: []
  },
  mutations: {
    set_token_expiry_date(state, expiryDate) {
      state.tokenExpiryDate=expiryDate;
    },
    auth_request(state) {
      state.status="loading";
    },
    auth_success(state, payload) {
      state.status="success";
      state.token=payload.token;

      // Use the expiry date calculated from backend expires_in
      if (payload.expiryDate) {
        state.tokenExpiryDate=payload.expiryDate;
        Cookies.set("tokenExpiryDate", payload.expiryDate, { expires: 365 });
      }

      Cookies.set("token", payload.token, { expires: 365 });
    }
    ,
    auth_error(state) {
      state.status="error";
    },
    setUser(state, user) {
      state.user=user.user;
    },
    logout(state) {
      state.status="";
      state.token="";
      state.tokenExpiryDate=null;
      state.notifications={};
      state.messages={};
      state.user={};
      Cookies.remove("token");
      Cookies.remove("tokenExpiryDate");
    },
    forget_password_request(state, user) {
      state.status="loading";
      state.user=user;
    },
    forget_password_success(state) {
      state.status="success";
    },
    forget_password_error(state) {
      state.status="error";
    },
    reset_password_request(state, user) {
      state.status="loading";
      state.user=user;
    },
    reset_password_success(state) {
      state.status="success";
    },
    reset_password_error(state) {
      state.status="error";
    },
    verify_email_request(state) {
      state.status="loading";
    },
    verify_email_success(state) {
      state.status="success";
    },
    verify_email_error(state) {
      state.status="error";
    },
    list_request(state) {
      state.status="loading";
    },
    list_success(state) {
      state.status="success";
    },
    list_error(state) {
      state.status="error";
    },
    setProduct(state, product) {
      state.product=product;
    },
    setProductName(state, name) {
      state.product.name=name;
    },
    setProductPictures(state, pictures) {
      state.product.pictures=pictures;
    },
    setProductCategory(state, category) {
      state.product.category=category;
    },
    setProductSalesType(state, salesType) {
      state.product.salesType=salesType;
    },
    setProductFixPrice(state, fixPrice) {
      state.product.fixPrice=fixPrice;
    },
    setProductBuyItNowPrice(state, buyItNowPrice) {
      state.product.buyItNowPrice=buyItNowPrice;
    },
    setProductLowestPrice(state, lowestPrice) {
      state.product.lowestPrice=lowestPrice;
    },
    setProductStartingPrice(state, startingPrice) {
      state.product.startingPrice=startingPrice;
    },
    setProductQuantity(state, quantity) {
      state.product.quantity=quantity;
    },
    setProductState(state, productState) {
      state.product.state=productState;
    },
    setProductDescription(state, description) {
      state.product.description=description;
    },
    setCategories(state, categories) {
      state.categories=categories.categories;
    },
    SET_CATEGORIES(state, payload) {
      state.categories=payload
      state.categoriesLoaded=true
    },
    SET_MESSAGES(state, payload) {
      state.messages=payload
      state.categoriesLoaded=true
    },
    setProductDuration(state, duration) {
      state.product.duration=duration;
    },
    setAttributes(state, attributes) {
      state.attributes=attributes;
    },
    setProductAttributes(state, attributes) {
      state.product.attributes=attributes;
    },
    setProductAcceptOffers(state, acceptOffers) {
      state.product.acceptOffers=acceptOffers;
    },
    setProductAcceptReturn(state, acceptReturn) {
      state.product.acceptReturn=acceptReturn;
    },
    setProductAcceptCashOnDelivery(state, acceptCashOnDelivery) {
      state.product.acceptCashOnDelivery=acceptCashOnDelivery;
    },
    setProductDeliveryCost(state, deliveryCost) {
      state.product.deliveryCost=deliveryCost;
    },
    setProductDeliveryTime(state, deliveryTime) {
      state.product.deliveryTime=deliveryTime;
    },
    setSearchFilter(state, filters) {
      state.searchFilter=filters
    },
    setSelectedCategory(state, selectedCategory) {
      state.selectedCategory=selectedCategory
    },
    setMessages(state, messages) {
      state.messages=messages.messages
    },
    updateMessages(state, messages) {
      state.messages=messages
      state.messagesLoaded=true
    },
    setNotifications(state, notifications) {
      state.notifications=notifications.notifications
    },
    setCurrentFilters(state, filters) {
      state.currentFilters=filters;
    },
    setCurrentCategory(state, category) {
      state.currentCategory=category;
    }
  },
  actions: {
    login({ commit }, user) {
      return new Promise((resolve, reject) => {
        commit("auth_request");
        api({
          url: "login",
          data: user,
          method: "POST",
        })
          .then((resp) => {
            const token=resp.data.data.token;
            // expires_in is in seconds, calculate actual expiry timestamp
            const currentTimeInSeconds=Math.floor(Date.now()/1000);
            const expiresIn=resp.data.data.expires_in;
            const expiryTimestamp=currentTimeInSeconds+expiresIn;
            const expiryDate=new Date(expiryTimestamp*1000).toISOString();

            axios.defaults.headers.common["Authorization"]="Bearer "+token;
            commit("auth_success", { token, expiryDate });
            resolve(resp);
          })
          .catch((err) => {
            commit("auth_error");
            Cookies.remove("token");
            reject(err);
          });
      });
    },

    register({ commit }, user) {
      return new Promise((resolve, reject) => {
        commit("auth_request");
        api({
          url: "register",
          data: user,
          method: "POST",
        })
          .then((resp) => {
            const token=resp.data.token;
            axios.defaults.headers.common["Authorization"]="Bearer "+token;
            commit("auth_success", token);
            resolve(resp);
          })
          .catch((err) => {
            commit("auth_error", err);
            Cookies.remove("token");
            reject(err);
          });
      });
    },

    logout({ commit }) {
      return new Promise((resolve) => {
        commit("logout");
        Cookies.remove("token");
        delete axios.defaults.headers.common["Authorization"];
        this.state.user={};
        this.state.tokenExpiryDate=null;
        resolve();
      });
    },
    forgetPassword({ commit }, user) {
      return new Promise((resolve, reject) => {
        commit("forget_password_request");
        api({
          url: "password/email",
          data: user,
          method: "POST",
        })
          .then((resp) => {
            commit("forget_password_success");
            resolve(resp);
          })
          .catch((err) => {
            commit("forget_password_error");
            reject(err);
          });
      });
    },
    resetPassword({ commit }, user) {
      return new Promise((resolve, reject) => {
        commit("reset_password_request");
        api({
          url: "password/reset",
          data: user,
          method: "POST",
        })
          .then((resp) => {
            commit("reset_password_success");
            resolve(resp);
          })
          .catch((err) => {
            commit("reset_password_error");
            reject(err);
          });
      });
    },
    verifyEmail({ commit }, data) {
      return new Promise((resolve, reject) => {
        commit("verify_email_request");
        //axios.defaults.baseURL = "http://localhost/";
        api({
          url: data.url,
          method: "GET",
        })
          .then((resp) => {
            commit("verify_email_success");
            resolve(resp);
          })
          .catch((err) => {
            commit("verify_email_error");
            reject(err);
          });
      });
    },
    getUsers({ commit }, roles) {
      return new Promise((resolve, reject) => {
        commit("list_request");
        api({
          url: "admin/list",
          data: roles,
          method: "POST",
          headers: {
            Authorization: "Bearer "+Cookies.get("token"),
          },
        })
          .then((resp) => {
            commit("list_success");
            resolve(resp);
          })
          .catch((err) => {
            commit("list_error");
            reject(err);
          });
      });
    },
    getTokenExpirationDate(encodedToken) {
      if (null===encodedToken) {
        return null;
      }

      let date=new Date(0);
      date.setUTCSeconds(encodedToken.exp);

      return date;
    },
    async updateMessages({ commit }) {
      try {
        const { data }=await axiosInstance.get('messages/unread')
        const messages=data.messages||data;
        // console.log(messages);

        commit('updateMessages', messages)
        return messages
      } catch (err) {
        console.error('Error updating messages:', err)
        return []
      }
    },
    async loadCategories({ state, commit }) {
      // 🚀 return cached if already fetched
      if (state.categoriesLoaded&&state.categories.length>0) {
        // return state.categories
      }

      try {
        const { data }=await api.get('product/list?has_been_sold=0')

        const categories=data.categories;

        const formatted=categories.map(cat => ({
          id: cat.id,
          label: cat.name,
          parent_id: cat.parent_id,
          href: `/products-filters?categories=${cat.id}`
        }))

        commit('SET_CATEGORIES', formatted)
        return formatted
      } catch (err) {
        console.error('Error loading categories:', err)
        return []
      }
    },
  },
  getters: {
    isLoggedIn: (state) => !!state.token,
    authStatus: (state) => state.status,
    categories: state => state.categories,
    messages: state => state.messages,
    isTokenValid(state) {
      const expiry=state.tokenExpiryDate||Cookies.get("tokenExpiryDate");
      const token=state.token||Cookies.get("token");
      const cookieToken=Cookies.get("token");

      if (expiry&&token&&cookieToken) {
        const expiryInSeconds=new Date(expiry).getTime()/1000;
        const currentTimeInSeconds=Date.now()/1000;

        return currentTimeInSeconds<expiryInSeconds;
      }
      return false;
    }

  },
  plugins: [
    createPersistedState({
      storage: window.localStorage,
    }),
  ],
});
